import {
  Account,
  Animal,
  CreatePersonaTask,
  DatabaseRecord,
  ImageReference,
  Mind,
  ModelProvider,
  Skill,
  User,
} from ".";
import { DocumentReference, Timestamp } from "@um/contracts/firestore";
import type { StudioImageModel, StudioImageThinkingLevel } from "./studioImageModels";

/**
 * Why an Animal was soft-deleted. Only `"user-retired"` is user-restorable;
 * `"reported"` and legacy soft-deletes with no reason stay hidden for good.
 */
export type AnimalDeleteReason = "user-retired" | "reported";

export const USER_RETIRED_DELETE_REASON: AnimalDeleteReason = "user-retired";
export const REPORTED_DELETE_REASON: AnimalDeleteReason = "reported";

export interface Character {
  name?: string;
  personas: Persona[];
  defaultPersonaId?: string;
  outfit?: OutfitOperation;
  outfitError?: string;
  isDeleted: boolean;
  isHidden: boolean;
  /** Set by the deleteCharacter callable; absent on legacy soft-deletes. */
  deletedReason?: AnimalDeleteReason;
  /**
   * Character made from a photo of the user and promoted to their profile
   * picture. Kept but hidden, and excluded from character-count points.
   */
  isSelfProfile?: boolean;
  imageModelProvider?: ModelProvider;
  relationships?: Relationship[];
  createdBy?: Agent;
}

/**
 * An item-free character version that can be the base of an outfit. Refined
 * versions are bases too. Must check both fields:
 * checking only `equippedItems` reads a v2 persona as unclothed.
 */
export function isBasePersona(persona: Persona): boolean {
  return !persona.wornItems?.length && !persona.equippedItems;
}

export function isPersonaContentBlocked(persona?: Persona): boolean {
  return (persona?.generationFailure?.code ?? persona?.errorCode ?? persona?.image?.errorCode) === "content_blocked";
}

/** Older tasks could recover their artwork without clearing a stale error field. */
function hasLegacyUsableArtwork(persona: Persona): boolean {
  const sheet = persona.imageVariations?.A;
  const portrait = persona.image;
  return !isApiManagedPersona(persona) && isBasePersona(persona) && !persona.generationStage &&
    !persona.generationFailure && !persona.errorCode && !persona.image?.errorCode &&
    ((!portrait?.error && !!(portrait?.large || portrait?.original)) ||
      (!sheet?.error && !!sheet?.panels && !!(sheet.large || sheet.original)));
}

export function hasPersonaGenerationFailed(persona?: Persona): boolean {
  if (!persona) return false;
  if (persona.generationFailure || isPersonaContentBlocked(persona)) return true;
  return !!(persona.status === "error" || persona.error || persona.errorCode || persona.image?.error) &&
    !hasLegacyUsableArtwork(persona);
}

export function isPersonaGenerationPending(persona?: Persona): boolean {
  if (!persona || hasPersonaGenerationFailed(persona)) return false;
  return !!((persona.generationStage && persona.generationStage !== "complete") ||
    (persona.status && persona.status !== "complete" && persona.status !== "error"));
}

/** Same source-state guard for creation previews, refinement and alternate-persona cloning. */
export function canUsePersonaAsSource(persona?: Persona): boolean {
  return !!persona && !persona.isDeleted && !hasPersonaGenerationFailed(persona) && !isPersonaGenerationPending(persona);
}

/** Failed fitting versions must never become refinement sources by recency. */
export function refinementSource(character: Character, selectedId?: string): Persona | undefined {
  const valid = (p?: Persona) => canUsePersonaAsSource(p) && !p?.isHidden;
  const selected = character.personas.find(p => p.id === selectedId);
  // Never silently refine a different picture than the one the user selected.
  if (selectedId && !valid(selected)) return undefined;
  const favorite = character.personas.find(p => p.id === character.defaultPersonaId);
  const candidate = valid(selected) ? selected : valid(favorite) ? favorite : [...character.personas].reverse().find(valid);
  const base = getOutfitBasePersona(character, candidate);
  return valid(base) ? base : undefined;
}

/**
 * Resolve the selected look's item-free version, not the first/oldest character
 * in its history. Stop at an item-free refinement even if it has its own source.
 * Legacy outfits may form a chain through `equippedItems` personas. Missing or
 * cyclic ancestry is unknown; never guess another character version as its base.
 */
export function getOutfitBasePersona(
  character: Pick<Character, "personas"> | undefined,
  persona: Persona | undefined
): Persona | undefined {
  const visited = new Set<string>();
  while (persona && !visited.has(persona.id)) {
    if (isBasePersona(persona)) return persona;
    visited.add(persona.id);
    const sourceId = persona.sourcePersonaId;
    persona = character?.personas?.find((p) => p.id === sourceId);
  }
  return undefined;
}

export interface Relationship {
  relationship: string;
  description?: string;
  agent: Agent;
}

export interface InteractivePersona {
  name?: string;
  imageVariations?: Record<string, ImageReference>;
  image?: ImageReference; // this may not be needed here for v2 personas
  /**
   * Size of this CHARACTER relative to the rest of the cast, default 1.
   *
   * Every character is drawn to fill its own frame, so on stage a lobster and
   * a dolphin come out the same size. This is the first of three factors that
   * multiply together (persona x sheet x panel) to fix that: it is the one that
   * says a dolphin is big and a lobster is small.
   */
  relativeScale?: number;
}

export interface GenerationFailure {
  code: "content_blocked" | "empty_response" | "invalid_grid" | "generation_failed";
  stage: "expression" | "showcase" | "portrait";
  retryable: boolean;
  providerReason?: string;
  model?: string;
}

export interface Persona extends InteractivePersona {
  generationStage?: GenerationFailure["stage"] | "complete";
  generationFailure?: GenerationFailure;
  outfitInputKey?: string;
  resumeFromPersonaId?: string;
  id: string;
  /** A meaningful look in a story ("Young", "Human", "Seal"), separate from technical refinements. */
  personaLabel?: string;
  /** Optional performance voice. Reading assistance remains a separate client feature. */
  voiceProfile?: import("./performanceSpeech").CharacterVoiceProfile;
  sourcePrompt?: string;
  promptSummary?: string; // deprecated
  appearance?: string;
  personality?: string;
  abilities?: Trait[];
  weaknesses?: Trait[];
  age?: string;
  gender?: string;
  backstory?: string;
  isHuman?: boolean;
  estimated?: Timestamp; // deprecated
  error?: string;
  /** Machine-readable error code (e.g. "content_blocked") set alongside `error`. */
  errorCode?: string;
  sourceImage?: ImageReference;
  /** Keep an approved source image's appearance when generating v2 poses. */
  preserveSourceAppearance?: boolean;
  /** Pinned at Studio character creation so all images in the job use one model. */
  studioImageModel?: StudioImageModel;
  studioImageThinkingLevel?: StudioImageThinkingLevel;
  sourcePersonaId?: string;
  task?: DocumentReference<CreatePersonaTask>;
  status?: string;
  isDeleted?: boolean;
  isHidden?: boolean;
  isUser?: boolean;
  iterationSuggestions?: string[]; // deprecated
  dateOfBirth?: Timestamp; // Date of birth stored as Firebase timestamp
  /**
   * The single item this version added. Kept for the v1 equip path and for
   * personas created before `wornItems` existed; v2 reads `wornItems`.
   */
  equippedItems?: DocumentReference<Item>;
  /**
   * The complete set of items this version wears, at most one per slot. A
   * version is a whole look, not a diff: every equip/swap/unequip regenerates
   * from the base persona with the full set.
   */
  wornItems?: DocumentReference<Item>[];
  imageModelProvider?: ModelProvider; // deprecated
  desiredImageVariations?: number; // deprecated (used by v1 persona generation)
  baselineSessionId?: string;
  defaultPoses?: DefaultPoses;
  /**
   * 1: single image + variations. 2: expression sheets made by the Firestore triggers.
   * 3: same schema and meaning as 2, but made and maintained by the `create` HTTP API: the
   * triggers never process it, and it may not have an A sheet yet (see `getPrimarySheet`).
   */
  version?: PersonaVersion;
  /**
   * Version 3: the id of the portrait (`image.id`) the `create` API drew on the design route, from `sourceImage` or
   * from the text alone. While the portrait is still that one, a later request draws the A sheet on the design route
   * too.
   */
  designLookId?: string;
  /** Job handler name offered when this persona is deployed as an ambassador */
  ambassadorJob?: string;
  /** Account-local Support id copied to public on publish; used by /view intro playback */
  introSupportId?: string;
  /** Formatted questionnaire answers applied as personality guidance */
  personalityHintsContext?: string;
  /** User finished the personality questionnaire (or skipped it) */
  personalityHintsFinalized?: boolean;
  /** User chose to skip personality questions */
  personalityHintsSkipped?: boolean;
  /** Characteristics have been regenerated using personalityHintsContext */
  personalityHintsApplied?: boolean;
  /** Hints finalized after initial characteristics; a refresh pass is pending */
  personalityHintsPendingRefresh?: boolean;
}

export type PersonaVersion = 1 | 2 | 3;

/** Personas made by the `create` API. The triggers leave them alone (no CreatePersonaTask). */
export const API_PERSONA_VERSION = 3 as const;

export function isApiManagedPersona(persona: { version?: number } | null | undefined): boolean {
  return (persona?.version ?? 1) >= API_PERSONA_VERSION;
}

/** v2 and later draw a character from expression sheets (`imageVariations`). */
export function isSheetPersona(persona: { version?: number } | null | undefined): boolean {
  return (persona?.version ?? 1) >= 2;
}

/** The identity sheet: 16 standard poses, A1..A16. */
export const IDENTITY_SHEET_KEY = "A";

const sheetHasImage = (sheet: ImageReference | undefined): sheet is ImageReference =>
  !!sheet && !sheet.error && !!(sheet.large || sheet.original || sheet.small);

/**
 * The sheet a character is drawn from: the A sheet when it exists, otherwise the first available sheet by key
 * (sheets with panels before plain variation images). Version-3 personas may not have an A sheet yet, so code that
 * needs "the character's sheet" should use this instead of `imageVariations.A`.
 */
export function getPrimarySheet(
  persona: Pick<InteractivePersona, "imageVariations"> | null | undefined
): { key: string; sheet: ImageReference } | undefined {
  const sheets = persona?.imageVariations;
  if (!sheets) return undefined;
  const a = sheets[IDENTITY_SHEET_KEY];
  if (sheetHasImage(a)) return { key: IDENTITY_SHEET_KEY, sheet: a };
  const available = Object.keys(sheets)
    .sort((x, y) => x.localeCompare(y))
    .filter((key) => sheetHasImage(sheets[key]));
  const key = available.find((k) => Object.keys(sheets[k].panels?.details ?? {}).length > 0) ?? available[0];
  return key ? { key, sheet: sheets[key] } : undefined;
}

/** The first enabled panel key of the primary sheet ("A1" for a v2 character), for pose defaults. */
export function getPrimaryPanelKey(
  persona: Pick<InteractivePersona, "imageVariations"> | null | undefined
): string | undefined {
  const primary = getPrimarySheet(persona);
  const details = primary?.sheet.panels?.details;
  if (!details) return undefined;
  return Object.keys(details)
    .filter((k) => !details[k].disabled)
    .sort((x, y) => {
      const nx = Number(x.replace(/^[A-Za-z]+/, "")),
        ny = Number(y.replace(/^[A-Za-z]+/, ""));
      return Number.isFinite(nx) && Number.isFinite(ny) && nx !== ny ? nx - ny : x.localeCompare(y);
    })[0];
}

/** The panel with this pose number on the primary sheet ("A16" -> "C16" when there is no A sheet), if it exists. */
export function primarySheetPanelKey(
  persona: Pick<InteractivePersona, "imageVariations"> | null | undefined,
  index: number
): string | undefined {
  const primary = getPrimarySheet(persona);
  const key = `${primary?.key}${index}`;
  return primary?.sheet.panels?.details?.[key] ? key : undefined;
}

// poses
export const wellKnownPoses = [
  "idle",
  "peeking",
  "thinking",
  "greeting",
  "bored",
  "sleeping",
  "listening",
  "pointing",
  "excited",
  "love",
  "notes",
  "laughing",
  "reportDuty",
  "facingRight",
] as const;

export type WellKnownPose = (typeof wellKnownPoses)[number];

export type DefaultPoses = Partial<Record<WellKnownPose, string>>;

export interface Trait {
  name: string;
  description?: string;
}

export type Agent = AnimalAgent | MindAgent | UserAgent | AgentBase;

export interface AgentBase {
  name: string;
  image?: ImageReference;
  // When `image` is a paneled expression sheet, which panel (e.g. "A16") to crop and display.
  imagePanelKey?: string;
}

export interface AnimalAgent extends AgentBase {
  animal: DocumentReference<Animal>;
  personaId?: string;
}

export interface MindAgent extends AgentBase {
  mind: DocumentReference<Mind>;
  personaId?: string;
}

export interface UserAgent extends AgentBase {
  user: DocumentReference<User>;
}

/**
 * What an item is for. `wearable` (the default when absent) is equipped onto a
 * character; `scene` is a place the character stands in and is never equipped.
 */
export type ItemKind = "wearable" | "scene" | "prop";

/**
 * Where a wearable goes. A character wears at most one item per slot, so
 * equipping a second hat replaces the first. Set when the item is created.
 *
 * `feet` was retired, but stored items may still carry it — `slotOf` coerces
 * anything unrecognised rather than indexing a slot table with it.
 */
export type ItemSlot = "head" | "body" | "accessory";

export const itemSlots: ItemSlot[] = ["head", "body", "accessory"];

/**
 * The showcase sheet ("B"). The backend draws the grid; the client plays panels
 * from it. Both ends must agree — a mismatch fails silently, dropping every
 * character to a single still.
 */
export const SHOWCASE = {
  /** Side length, not count — 4 gives B1–B16. */
  dimensions: 4,
  /** Leading panels that are resting poses; the animation returns to one between poses. */
  restPanels: 2,
} as const;

/** Every panel on the sheet. */
export const showcasePanelCount = SHOWCASE.dimensions * SHOWCASE.dimensions;

/** The last panel, carrying the captain's uniform. Kept out of the animation. */
export const showcaseCaptainPanel = `B${showcasePanelCount}`;

/** The panels the client animates — the rests plus every pose but the captain. */
export const showcaseAnimatedPanels = showcasePanelCount - 1;

/** Reward tiers. Only two, since accounts hold few enough items that a finer scale says nothing. */
export type ItemRarity = "standard" | "rare";

/**
 * Closed vocabularies for what an item is, so items generated from different
 * prompts can still be compared across accounts. Keep them small and closed:
 * open-ended tags split one idea across synonyms and never aggregate.
 */
export const itemCategories = ["cap", "scarf", "glasses", "jumper", "tee", "coat", "bag", "badge"] as const;
export type ItemCategory = (typeof itemCategories)[number];

export const itemRegisters = ["cosy", "smart", "sporty", "whimsical", "rugged", "elegant"] as const;
export type ItemRegister = (typeof itemRegisters)[number];

export const itemPalettes = ["warm", "cool", "bright", "muted", "monochrome"] as const;
export type ItemPalette = (typeof itemPalettes)[number];

export const itemMotifs = ["floral", "nautical", "celestial", "animal", "geometric", "none"] as const;
export type ItemMotif = (typeof itemMotifs)[number];

/** How the generator classified an item, alongside its name and description. */
export interface ItemFacets {
  category?: ItemCategory;
  register?: ItemRegister;
  palette?: ItemPalette;
  motif?: ItemMotif;
}

/**
 * The item ids a persona is wearing, tolerating the old singular field.
 *
 * The only truthful answer to "is this item worn". `Item` once declared an
 * `equippedBy` that looked like the field for this; nothing ever wrote it, so
 * anything reading it reported nothing worn, forever. It has been removed —
 * wear lives on the wearer, never on the item.
 */
export function wornItemIds(persona: Persona | undefined): string[] {
  if (persona?.wornItems?.length) return persona.wornItems.map((ref) => ref.id);
  if (persona?.equippedItems) return [persona.equippedItems.id];
  return [];
}

export interface Item extends DatabaseRecord {
  photoTrial?: { mindId: string; previewId: string };
  /** Original earned reward for this separate, server-managed photo replacement. */
  photoRewardOriginId?: string;
  replacedBy?: string;
  /** Public template used to create this character's owned wearable copy. */
  catalogSource?: DocumentReference<Item>;
  /** Explicit public-catalog opt-in; never copied onto an account's claimed prize. */
  prizeMachine?: { enabled: boolean; category: "adventure" | "dressup" | "music" };
  /** Reviewed narrative affordances; story snapshots never mutate reward ownership. */
  story?: import("./storyItems").StoryItemProperties;
  /** Opt-in from ladder clients whose copy allows either clothing or headwear. */
  catalogRewardEligible?: boolean;
  /** Written by the server after a fixed reward has been assigned. */
  rewardExperiment?: import("./rewardExperiment").RewardExperimentStamp;
  earnedVia?: import("./itemRewards").ItemEarnedVia;
  replacementDraftId?: string;
  /** Server-owned reservation/current wearer. */
  wear?: { characterId: string; operationId: string; state: "reserved" | "worn" };
  name: string;
  description: string;
  /** Defaults to `wearable` when absent, for items created before scenes. */
  kind?: ItemKind;
  /** Wearables only. Absent on items created before slots existed. */
  slot?: ItemSlot;
  /** Short action for an accessory, e.g. "Play guitar". */
  interactionLabel?: string;
  /**
   * Absent means `standard`; only the optional Understand capstone grants
   * `rare`. Rarity changes the generation brief and lengthens the delivery.
   */
  rarity?: ItemRarity;
  /** When the user opened the package. Unopened items are hidden from the wardrobe. */
  openedAt?: Timestamp;
  /** First confirmed wear; preserved after unequipping. Absent on older items. */
  firstWornAt?: Timestamp;
  /** Legacy reveal rating; no longer collected. */
  rating?: "liked" | "disliked";
  /** Timestamp of a legacy rating. */
  ratedAt?: Timestamp;
  /**
   * When the parcel is due. Generation takes about a minute; this is set much
   * later, by how much depending on what was made. The wait is the point.
   */
  deliverAt?: Timestamp;
  /**
   * When the "still unwrapped?" nudge went out, for a parcel that arrived and
   * was left closed. Set once and never cleared, so the reminder happens at
   * most once per parcel — a child who is not interested should not be asked
   * again every day.
   */
  unopenedNudgedAt?: Timestamp;
  /** When the unworn reminder was processed, including skips without a push. */
  unwornNudgedAt?: Timestamp;
  /**
   * When the "it's arrived" push went out. Also the idempotency guard for
   * `onScheduleItemDelivery` — the schedule can overlap its own runs, so this
   * is what stops a second notification rather than any in-memory bookkeeping.
   */
  arrivedNotifiedAt?: Timestamp;
  /**
   * The survey wish this item was granted for, in the user's own words.
   *
   * Present only on items an admin sent in answer to a wish. It marks the item
   * as one to reveal with "your wish came true" rather than through the ordinary
   * parcel tracker, and it is the text quoted back to them — so it is stored
   * verbatim rather than reconstructed from `sourcePrompt`, which the generator
   * rewrites.
   */
  wish?: string;
  /**
   * When the "your wish came true" reveal was shown and acknowledged.
   *
   * Separate from `openedAt` on purpose. Equipping from the reveal opens the
   * item outright; dismissing it should not, or a mistaken tap outside the
   * dialog would silently file the item in the wardrobe with nothing left to
   * find it by — a wish item is deliberately not a parcel until this is set.
   * Once it is, the item rejoins the ordinary tracker as "Ready to open".
   */
  wishRevealedAt?: Timestamp;
  /** Where it ships from, in the generator's words. Stored but not shown — it can come out wrong. */
  origin?: string;
  sourceImage?: ImageReference;
  sourcePrompt?: string;
  /**
   * The companion who brings the parcel: the sender on the delivery
   * notification and where the ladder finale sends you. Despite the name it
   * does **not** style the item — nothing does. An item is a thing any of them
   * can wear, so no character reaches its brief or its art.
   */
  styleOf?: DocumentReference<Animal>;
  image: ImageReference;
  /**
   * When generation gave up on this item.
   *
   * The failure is otherwise invisible: a parcel that never gets a name, art or
   * `deliverAt` is skipped by the delivery job (a document missing the field is
   * not in that field's index), and nothing rewrites it, so `onItemCreated`
   * never fires again either. Written so the item can at least be found —
   * nothing retries off it yet.
   */
  generationFailedAt?: Timestamp;
  generationAttemptId?: string;
  generationStartedAttemptId?: string;
  /** Absent on older items and never backfilled — treat missing as "unknown", not a default. */
  facets?: ItemFacets;
  abilities?: Trait[]; // for later
  weaknesses?: Trait[]; // for later
  mind?: DocumentReference<Mind>;
  skills?: DocumentReference<Skill>[];
  isGenerated?: boolean;
}

export interface OutfitOperation {
  id: string;
  personaId: string;
  itemIds: string[];
  previousItemIds: string[];
  phase: "rebuilding" | "dressing";
}

export interface ChangeOutfitRequest {
  /** Admin-only atomic addition: preserves other slots in the current outfit. */
  equipItemId?: string;
  /** Direct equip interaction also establishes an opportunity, even before the view timer fires. */
  preferenceContext?: { itemId: string; position: number; favoritesFilter: boolean };
  /** Browsing profile, including for older items with no owner profile. */
  mindId?: string;
  accountId: string;
  characterId: string;
  requestId: string;
  itemIds?: string[];
  personaId?: string;
  cancel?: boolean;
}
export interface ChangeOutfitResponse {
  personaId: string;
  pending: boolean;
}
export interface AdminEquipItemRequest {
  sourceAccountId: string;
  itemId: string;
  accountId: string;
  characterId: string;
  requestId: string;
}
export interface AdminEquipItemResponse extends ChangeOutfitResponse {
  itemId: string;
}
export interface EditPersonaRequest {
  accountId: string;
  characterId: string;
  personaId: string;
  kind?: "character" | "mind";
  action: "add" | "update" | "delete" | "clone" | "restoreImage";
  patch?: Partial<Persona>;
  imageSelection?: {
    imageId: string;
    historyRecordId?: string;
    target: "image" | "sourceImage" | "imageVariations";
    sheetLetter?: string;
    expectedImageId: string | null;
    sourcePersonaId?: string;
  };
}

/** Admin-only preview, kept outside the user's Item collection until approved. */
export interface WishDraft extends Item {
  reviewStatus: "pending" | "approved" | "recalled" | "superseded" | "replaced";
  replacesItemId?: string;
  replacedByItemId?: string;
  generationStartedAt?: Timestamp;
  approvedAt?: Timestamp;
  approvedBy?: string;
  recalledAt?: Timestamp;
  recalledBy?: string;
  supersededBy?: string;
}

export function canReplaceWishItem(item: Item | undefined, now = Date.now()): boolean {
  return (
    !!item?.wish &&
    !item.openedAt &&
    !item.wear &&
    !item.generationFailedAt &&
    !item.image?.error &&
    !!(item.image?.original || item.image?.large || item.image?.small) &&
    !!item.deliverAt &&
    item.deliverAt.toMillis() <= now
  );
}

/** A recall removes the live item, but its current replacement can still be sent. */
export function canSendRecalledWishReplacement(
  original: WishDraft | undefined,
  draft: Pick<WishDraft, "mind" | "wish" | "reviewStatus">,
  draftId: string
): boolean {
  return (
    !!original &&
    original.reviewStatus === "recalled" &&
    !original.supersededBy &&
    !original.openedAt &&
    !original.wear &&
    original.replacementDraftId === draftId &&
    draft.reviewStatus === "pending" &&
    !!draft.mind &&
    original.mind?.path === draft.mind.path &&
    !!draft.wish?.trim() &&
    original.wish?.trim() === draft.wish.trim()
  );
}
